import { useEffect, useMemo, useState } from 'react'
import { AtSign, LayoutGrid, Mail } from 'lucide-react'
import Button from '../../components/Button'
import FormRow from '../../components/FormRow'
import IdentityBand from '../../components/IdentityBand'
import PageHeader from '../../components/PageHeader'
import Select from '../../components/Select'
import PageLoader from '../../components/PageLoader'
import { useToast } from '../../components/toastContext'
import TextField from '../../components/TextField'
import { getProfile, updateProfile } from './accountService'
import { profileStrings as S } from './strings'
import './account.css'

/**
 * ملف المستخدم — Views/UserProfile/Index.cshtml.
 *
 * Which rows appear and which are editable comes straight from ProfileFieldsSetup, so
 * the screen reflects whatever إعدادات الملف الشخصي is set to.
 */
export default function ProfilePage() {
  const toast = useToast()
  const [data, setData] = useState(null)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getProfile().then((response) => {
      setData(response)
      setForm(response.profile ?? {})
    })
  }, [])

  const set = (key) => (value) => setForm((previous) => ({ ...previous, [key]: value }))

  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    const response = await updateProfile(form)
    setSaving(false)
    if (response.ok) toast.success(S.saved)
    else toast.error(S.saved)
  }

  /** A hidden field is `display:none` in the legacy, not disabled — it simply isn't there. */
  const rows = useMemo(() => {
    const setup = new Map((data?.fields ?? []).map((field) => [field.name, field]))
    return S.order
      .map((name) => ({ name, ...(setup.get(name) ?? { isVisible: false, isEditable: false }) }))
      .filter((field) => field.isVisible)
  }, [data])

  const groupOptions = useMemo(
    () => (data?.groups ?? []).map((group) => ({ value: group.id, label: group.name })),
    [data],
  )

  const departmentName = useMemo(
    () => (data?.groups ?? []).find((group) => group.id === form?.DepartmentId)?.name ?? '',
    [data, form],
  )

  /** Attribute1..5 wear the name configured under إعدادات متقدمة. */
  const labelFor = (name) =>
    S.fields[name] ?? data?.settings?.[`${name}_AR_Name`] ?? name

  if (!form) {
    return (
      <>
        {/* The guidance paragraph is three lines of prose you read once and then
          scroll past forever. Behind the "i" it stays one hover from the title
          and the form starts at the top of the page. */}
      <PageHeader title={S.title} hint={S.subtitle} />
        <PageLoader />
      </>
    )
  }

  return (
    <>
      {/* The guidance paragraph is three lines of prose you read once and then
          scroll past forever. Behind the "i" it stays one hover from the title
          and the form starts at the top of the page. */}
      <PageHeader title={S.title} hint={S.subtitle} />

      {/*
       * Whose profile this is.
       *
       * The page opened straight onto six inputs — the one screen in the panel
       * that is ABOUT you told you nothing about you, and left the lower half of
       * the viewport empty besides. The band costs no request: every value on it
       * is already in the payload the form is bound to.
       */}
      <IdentityBand
        name={form.ArabicName || form.Username}
        meta={[
          { icon: AtSign, text: form.Username, ltr: true },
          ...(form.Email ? [{ icon: Mail, text: form.Email, ltr: true }] : []),
          ...(departmentName ? [{ icon: LayoutGrid, text: departmentName }] : []),
        ]}
      />

      <form className="acct-panel" onSubmit={submit}>
        <div className="acct-fields">
          {rows.map((field) => (
            <FormRow key={field.name} label={labelFor(field.name)}>
              {(id) =>
                field.name === 'Gender' ? (
                  <Select
                    id={id}
                    value={form.Gender}
                    onChange={set('Gender')}
                    options={S.genders}
                    disabled={!field.isEditable}
                  />
                ) : field.name === 'DepartmentId' ? (
                  <Select
                    id={id}
                    value={form.DepartmentId}
                    onChange={set('DepartmentId')}
                    options={groupOptions}
                    disabled={!field.isEditable}
                  />
                ) : (
                  <TextField
                    id={id}
                    type={field.name === 'Email' ? 'email' : 'text'}
                    value={form[field.name] ?? ''}
                    onChange={set(field.name)}
                    disabled={!field.isEditable}
                    required={S.required.includes(field.name)}
                    dir={field.name === 'ArabicName' ? undefined : 'ltr'}
                    autoComplete="off"
                  />
                )
              }
            </FormRow>
          ))}
        </div>

        <div className="acct-panel__footer">
          <Button type="submit" disabled={saving}>
            {saving ? '…' : S.save}
          </Button>
        </div>
      </form>

    </>
  )
}
